// Generated by tutorials/record.cjs from tutorials/articles/h04-ehr-page.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h04 --text-only` (wording) or
// `node tutorials/record.cjs h04` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'ehr-page',
  catalogId: 'h04',
  title: 'The EHR page, tab by tab',
  summary: 'The EHR page shows everything your EHR sends Dietech: messages that need you, every message already processed, allergy changes and added items to review, and the mappings behind it all. Here is what each tab is for and what to do there.',
  audience: 'Dietary managers and dietitians at facilities connected to their EHR',
  area: 'residents',
  learn: [
    'See which EHR messages need you on Pending Messages, and why',
    'Open a message and map a unit Dietech does not know yet',
    'Look back through Message History, for everyone or one resident',
    'Know what Allergy Changes and Added Items are for',
    'Find the unit, exclusion and room and bed mappings',
  ],
  keywords: [
    'ehr',
    'emr',
    'ehr page',
    'pending messages',
    'message history',
    'action needed',
    'needs mapping',
    'needs a resident match',
    'waiting on earlier message',
    'unit mapping',
    'map unit',
    'exclusions',
    'room/bed mappings',
    'added items',
    'allergy changes',
    'hl7',
    'interface',
  ],
  steps: [
    {
      id: 'open',
      part: 'Work through Pending Messages',
      heading: 'Open the EHR page',
      text: 'In the menu, select Residents, then EHR. The page opens on Pending Messages: messages from your EHR that have not gone through yet.\n\nMost go through on their own within a few minutes. When some need you, the page shows just those, under Action Needed.',
      media: {
        image: {
          src: '/help/ehr-page/open.webp',
          width: 1280,
          height: 684,
        },
        alt: 'The EHR page on Pending Messages, with Action Needed selected and three messages listed.',
      },
    },
    {
      id: 'summary',
      part: 'Work through Pending Messages',
      heading: 'Read what each one needs',
      text: 'The box above the list says how many messages need action and what to do about each kind. The Action column says the same for each row:\n\n- Needs a resident match: your EHR sent an update for someone Dietech cannot find. Open it and choose the resident, or add them as new.\n- Needs mapping: part of the message is not linked to Dietech yet. Open it and map what is marked in red.\n- Waiting on earlier message: it goes through by itself once the earlier message for the same resident is fixed.',
      media: {
        image: {
          src: '/help/ehr-page/summary.webp',
          width: 1280,
          height: 684,
        },
        alt: 'The box says 3 messages need action: one does not match a resident, one has values that need mapping, and one is waiting behind an earlier message.',
      },
    },
    {
      id: 'view',
      part: 'Work through Pending Messages',
      heading: 'Open a message',
      text: 'Select a message, then View. The message opens with its sections down the left: General Information, Location, Diet Order and Food Allergies.\n\nWhat needs mapping is marked in red. Here, your EHR moved Dolores Navarro to 2 WEST, a unit Dietech does not know yet.',
      media: {
        image: {
          src: '/help/ehr-page/view.webp',
          width: 1280,
          height: 874,
        },
        clip: {
          src: '/help/ehr-page/view.mp4',
          width: 1280,
          height: 874,
          durationSec: 2.1,
        },
        alt: 'The message for NAVARRO, DOLORES: Location is red, saying Matching unit not found, with a Map Unit button.',
      },
    },
    {
      id: 'map-unit',
      part: 'Work through Pending Messages',
      heading: 'Map the unit',
      text: 'Select Map Unit. The window shows the location your EHR sent. Pick the Dietech unit it belongs to.',
      media: {
        image: {
          src: '/help/ehr-page/map-unit.webp',
          width: 942,
          height: 942,
        },
        clip: {
          src: '/help/ehr-page/map-unit.mp4',
          width: 942,
          height: 942,
          durationSec: 3,
        },
        alt: 'The Unit Mapping window: the EHR location Floor 2, Wing W, Unit 2 WEST, with the unit Willow picked.',
      },
    },
    {
      id: 'ok',
      part: 'Work through Pending Messages',
      heading: 'Confirm, then select OK',
      text: 'Select Confirm Match. The mapping is saved, so later messages for 2 WEST go to that unit by themselves. You can see it under Mappings, Unit Mappings.\n\nWith nothing left in red, select OK. The message is sent for processing and goes through within a few minutes. The order that was waiting behind it goes through after it.',
      media: {
        image: {
          src: '/help/ehr-page/ok.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-page/ok.mp4',
          width: 1280,
          height: 684,
          durationSec: 5,
        },
        alt: 'Back on Pending Messages: a note says the message for NAVARRO, DOLORES was sent for processing, and only the message that needs a resident match is left under Action Needed.',
      },
    },
    {
      id: 'history',
      part: 'Look back in Message History',
      heading: 'Open Message History',
      text: 'Select Message History. It lists every message that has gone through, newest first: the resident, their unit and room, the Message Type, when it was processed, and whether it carried a diet order.\n\nIt opens on the last 24 hours. Select 2 Weeks, 1 Month or All to look further back; each shows how many messages it holds.',
      media: {
        image: {
          src: '/help/ehr-page/history.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-page/history.mp4',
          width: 1280,
          height: 684,
          durationSec: 3.7,
        },
        alt: 'Message History for the last 24 hours: orders, an admission, a transfer, a leave and a return, each with its time processed.',
      },
    },
    {
      id: 'search',
      part: 'Look back in Message History',
      heading: 'Look further back, or search',
      text: 'Select 2 Weeks to see more, then type in Search to narrow the list by name, ID, unit or room.',
      media: {
        image: {
          src: '/help/ehr-page/search.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-page/search.mp4',
          width: 1280,
          height: 684,
          durationSec: 5.9,
        },
        alt: 'Message History for 2 weeks, narrowed to NAVARRO by typing in Search: one order from nine days ago.',
      },
    },
    {
      id: 'view-history',
      part: 'Look back in Message History',
      heading: 'See exactly what was sent',
      text: 'Select a message, then View, to see what your EHR sent: who it was for, the location, the diet order and the food allergies, as they arrived. Here nothing can be changed. Select Close when you are done.',
      media: {
        image: {
          src: '/help/ehr-page/view-history.webp',
          width: 1280,
          height: 874,
        },
        clip: {
          src: '/help/ehr-page/view-history.mp4',
          width: 1280,
          height: 874,
          durationSec: 2,
        },
        alt: 'The Order message for NAVARRO, DOLORES, read only: General Information, Location, the diet order REGULAR, MINCED AND MOIST, MILDLY THICK, and Food Allergies None.',
      },
    },
    {
      id: 'resident-history',
      part: 'Look back in Message History',
      heading: 'One resident\'s messages',
      text: 'To see every message for one resident, from any date, select their message, then Resident History. The list shows just theirs, and Open Resident EHR Tab takes you to the EHR tab on their record. Select the chip with their name to go back to everyone.',
      media: {
        image: {
          src: '/help/ehr-page/resident-history.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-page/resident-history.mp4',
          width: 1280,
          height: 684,
          durationSec: 6,
        },
        alt: 'Message History showing only NAVARRO, DOLORES\'s messages, all dates, with the chip Navarro, Dolores, all messages.',
      },
    },
    {
      id: 'allergy-changes',
      part: 'Allergy Changes and Added Items',
      heading: 'Allergy Changes',
      text: 'Allergy Changes lists every food allergy your EHR added to a resident or took off, so a dietitian can check each one. The number on the tab is how many are waiting.\n\nCheck the ones you have looked at, then select Mark Reviewed. They move to Reviewed, with your name and the time. The article Map EHR text to Dietech shows it step by step.',
      media: {
        image: {
          src: '/help/ehr-page/allergy-changes.webp',
          width: 1280,
          height: 684,
        },
        alt: 'The Allergy Changes tab: allergies added and one removed, each with the resident, the allergy and when.',
      },
    },
    {
      id: 'added-items',
      part: 'Allergy Changes and Added Items',
      heading: 'Added Items',
      text: 'Added Items lists what your EHR gave a resident or ended: supplements, tube feeds, global adjustments and meal patterns, and action plans. It only fills up once Dietech support has turned these on for your facility.',
      media: {
        image: {
          src: '/help/ehr-page/added-items.webp',
          width: 1280,
          height: 684,
        },
        alt: 'The Added Items tab: action plans, supplements and a tube feed, each with the resident, the EHR order and what happened.',
      },
    },
    {
      id: 'what-happened',
      part: 'Allergy Changes and Added Items',
      heading: 'Read What happened',
      text: 'What happened says what Dietech did, with a line explaining it: Added; Added, then ended; Already there, when staff had given it first; Changed by staff, when someone edited it, so the EHR leaves it alone. Left open means staff edited an action plan, so it stayed open when the order ended. Check those first.\n\nCheck the rows you have looked at, then select Mark Reviewed.',
      media: {
        image: {
          src: '/help/ehr-page/what-happened.webp',
          width: 1280,
          height: 568,
        },
        alt: 'The What happened column: Added, Left open: please review, Added then ended, and Already there, each with its explanation.',
      },
    },
    {
      id: 'mappings',
      part: 'Find your way around Mappings',
      heading: 'Open Mappings',
      text: 'Select Mappings. Across the top are its lists: Mappings (the text your EHR sends and what it means), Frequencies, Exclusions, Unit Mappings, Room/Bed Mappings and Mapping health.\n\nThe banner says how much text is waiting to be mapped. Map waiting text opens the Map this pop-up.',
      media: {
        image: {
          src: '/help/ehr-page/mappings.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-page/mappings.mp4',
          width: 1280,
          height: 684,
          durationSec: 3.2,
        },
        alt: 'The Mappings tab: a banner about 2 phrases waiting, the lists Mappings, Frequencies, Exclusions, Unit Mappings, Room/Bed Mappings and Mapping health, and the Diet Type mappings.',
      },
    },
    {
      id: 'units',
      part: 'Find your way around Mappings',
      heading: 'Unit Mappings',
      text: 'Unit Mappings says which Dietech unit each EHR floor, wing and unit belongs to. The 2 WEST mapping made from the message is here.\n\nAutomatic discharge Yes means that when an update moves a resident to that location, such as a hospital leave unit, they are discharged.',
      media: {
        image: {
          src: '/help/ehr-page/units.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-page/units.mp4',
          width: 1280,
          height: 684,
          durationSec: 3.1,
        },
        alt: 'Unit Mappings: MAPLE, BIRCH, WILLOW and 2 WEST with their Dietech units, and HOSPITAL LEAVE with Automatic discharge Yes.',
      },
    },
    {
      id: 'exclusions',
      part: 'Find your way around Mappings',
      heading: 'Exclusions',
      text: 'Exclusions lists EHR units whose messages Dietech does not process, such as an outpatient clinic Dietech does not serve. Select New to add one.',
      media: {
        image: {
          src: '/help/ehr-page/exclusions.webp',
          width: 1280,
          height: 684,
        },
        alt: 'Exclusions: OUTPATIENT REHAB and ADULT DAY.',
      },
    },
    {
      id: 'room-beds',
      part: 'Find your way around Mappings',
      heading: 'Room/Bed Mappings',
      text: 'Room/Bed Mappings turns the room and bed your EHR sends into Dietech\'s, when the two name them differently, such as 101A for room 101, bed A.\n\nSelect a row, then Edit or History. Frequencies has its own article, and Mapping health is in Map EHR text to Dietech.',
      media: {
        image: {
          src: '/help/ehr-page/room-beds.webp',
          width: 1280,
          height: 684,
        },
        alt: 'Room/Bed Mappings: EHR room 101A becomes room 101, bed A, and 101B becomes room 101, bed B.',
      },
    },
  ],
  tips: [
    'The EHR item in the menu shows a number when messages need you. It is the same count as Action Needed.',
    'Pending Messages refreshes by itself every minute or so. The time it last refreshed is at the right of the toolbar, with a button to pause it.',
    'Search on Message History looks in the list you are showing. If a name is not found, select a longer time range, such as 1 Month or All.',
  ],
  notes: [
    'Most messages go through on their own within a few minutes. Only the ones under Action Needed wait for someone.',
    'A message that failed to process is listed under Errors, a filter that appears only when there are some. Select it, then Resubmit Error to try it again. If it fails again, call Dietech support.',
    'Opening a message needs an EHR level above View on your role, and Delete needs a higher level still. The Mappings tab shows only when your role includes diet order or allergy mapping. Ask your administrator.',
    'The clocks at the top of the page show the time in UTC, at your facility and on your computer. A note appears there if your EHR\'s clock does not agree with your facility\'s.',
  ],
  related: [
    'map-ehr-text',
    'ehr-supplements-tube-feeds',
    'ehr-connection',
    'ehr-frequencies-action-plans',
    'ehr-held-diet-orders',
  ],
  recorded: '2026-11-03',
};

export default article;
